import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { ButtonComponent, IconComponent } from '../../../shared/ui';
import { IconName } from '../../../shared/ui/icon/icon.registry';
import { CustomSelectComponent, SelectOption } from '../../../shared/ui/custom-select/custom-select.component';
import { LastModuleTracker } from '../../data-access/last-module.tracker';
import { AuthService } from '../../../auth/data-access/auth.service';
import { IntegrationsManagementApi } from '../../../integrations/data-access/integrations-management.api';
import { TaxCenterApi } from '../../../tax-center/data-access/tax-center.api';
import { UserConnection } from '../../../integrations/models/integrations-management.models';
import { SupportApi } from '../../data-access/support.api';
import { captureEnvironment, EnvironmentContext } from '../../data-access/environment-context';
import { environment } from '../../../../environments/environment';
import {
  ACCEPTED_MIMES,
  CreateIssueRequest,
  CreatedIssue,
  IssueContext,
  IssueModule,
  IssueType,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENT_TOTAL_BYTES,
  MODULE_OPTIONS,
  UploadedAttachment
} from '../../models/support.models';

interface PendingAttachment {
  file: File;
  previewUrl: string;
  uploading: boolean;
  error?: string;
  uploaded?: UploadedAttachment;
}

interface TypeChoice {
  value: IssueType;
  label: string;
  icon: IconName;
}

/**
 * The two journeys. "Something's broken" and "I have an idea" ask for genuinely different
 * things — a feature request has no steps to reproduce and no expected-vs-actual — so the
 * split is not cosmetic: it drives which half of the form renders at all.
 */
const BROKEN_TYPES: TypeChoice[] = [
  { value: 'bug', label: 'Bug', icon: 'bug' },
  { value: 'ui_issue', label: 'UI Issue', icon: 'monitor' },
  { value: 'performance_issue', label: 'Performance', icon: 'zap' },
  { value: 'sync_issue', label: 'Sync Issue', icon: 'refresh-cw' },
  { value: 'pricing_issue', label: 'Pricing Issue', icon: 'dollar-sign' },
  { value: 'tax_calculation_issue', label: 'Tax Calculation', icon: 'calculator' },
];

const IDEA_TYPES: TypeChoice[] = [
  { value: 'feature_request', label: 'Feature Request', icon: 'sparkles' },
  { value: 'improvement_suggestion', label: 'Improvement', icon: 'trending-up' },
  { value: 'other', label: 'Other', icon: 'help-circle' },
];

const IDEA_TYPE_VALUES = new Set<IssueType>(IDEA_TYPES.map((t) => t.value));

/**
 * The steps box seeds itself with "1. " so the auto-numbering has something to continue
 * from. That seed is scaffolding, not content: a user who tabs through the field and never
 * types must not end up filing a report whose only reproduction step is "1.".
 */
const STEPS_PLACEHOLDER_ONLY = /^\s*\d+\.\s*$/;

/**
 * The issue report form. Used by BOTH the in-page modal and the standalone Support
 * page, so the form (and its validation, consent and upload logic) exists once.
 */
@Component({
  selector: 'app-report-issue-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, IconComponent, CustomSelectComponent],
  templateUrl: './report-issue-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportIssueFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(SupportApi);
  private readonly toast = inject(ToastrService);
  private readonly auth = inject(AuthService);
  private readonly integrationsApi = inject(IntegrationsManagementApi);
  private readonly taxApi = inject(TaxCenterApi);
  private readonly lastModuleTracker = inject(LastModuleTracker);
  private readonly destroyRef = inject(DestroyRef);

  /** Pre-filled from the current route when opened via the in-page button. */
  readonly initialModule = input<IssueModule | null>(null);

  readonly submitted = output<CreatedIssue>();
  readonly cancelled = output<void>();

  readonly brokenTypes = BROKEN_TYPES;
  readonly ideaTypes = IDEA_TYPES;
  // "" is a real option, not just the placeholder — the module field is optional, so the
  // user must be able to go back to "not specified" after picking one.
  readonly moduleOptions: SelectOption[] = [
    { value: '', label: 'Not specified' },
    ...MODULE_OPTIONS
  ];
  readonly maxAttachments = MAX_ATTACHMENTS;
  readonly isDragging = signal(false);
  readonly acceptAttr = ACCEPTED_MIMES.join(',');

  readonly submitting = signal(false);
  readonly attachments = signal<PendingAttachment[]>([]);
  // Expanded by default — collapsed at the bottom of a four-screen form, the disclosure was
  // invisible to precisely the people it exists to reassure.
  readonly detailsExpanded = signal(true);
  readonly connections = signal<UserConnection[]>([]);
  readonly taxYears = signal<number[]>([]);


  readonly env: EnvironmentContext = captureEnvironment(environment.appVersion);

  readonly form = this.fb.group({
    title: ['', [Validators.required, Validators.maxLength(140)]],
    issue_type: ['bug' as IssueType, [Validators.required]],
    description: ['', [Validators.required, Validators.maxLength(5000)]],
    module: [''],
    steps_to_reproduce: ['', [Validators.maxLength(5000)]],
    expected_behavior: ['', [Validators.maxLength(5000)]],
    actual_behavior: ['', [Validators.maxLength(5000)]],
    contact_email_hint: ['', [Validators.email]],
    technical_details_consent: [false],

    // Conditional context — required-ness is applied dynamically in onTypeChange().
    // A string, not a number: app-select is a ControlValueAccessor over strings. The
    // empty string is what Validators.required rejects, and buildContext() casts back.
    tax_year: [''],
    jurisdiction: [''],
    report_id: [''],
    connection_id: [''],
    asset: [''],
    date: [''],
    expected_price_source: [''],
    occurred_at: ['']
  });

  readonly issueType = signal<IssueType>('bug');

  /** app-select values are strings; buildContext() casts the year back with Number(). */
  readonly taxYearOptions = computed<SelectOption[]>(() =>
    this.taxYears().map((y) => ({ value: String(y), label: String(y) }))
  );

  readonly connectionOptions = computed<SelectOption[]>(() =>
    this.connections().map((c) => ({
      value: c._id,
      label: `${c.label} (${c.connection_type})`
    }))
  );

  private readonly titleValue = toSignal(this.form.controls.title.valueChanges, { initialValue: '' });
  private readonly descriptionValue = toSignal(this.form.controls.description.valueChanges, { initialValue: '' });
  private readonly stepsValue = toSignal(this.form.controls.steps_to_reproduce.valueChanges, { initialValue: '' });
  private readonly moduleValue = toSignal(this.form.controls.module.valueChanges, { initialValue: '' });

  readonly titleLength = computed(() => (this.titleValue() ?? '').length);
  readonly descriptionLength = computed(() => (this.descriptionValue() ?? '').length);

  /**
   * Counters appear only in the last 20% of the budget. Showing "0/5000" from the outset
   * reads as "we expect an essay" and suppresses short, perfectly good reports; the count
   * is only ever useful when you're near the ceiling.
   */
  readonly showTitleCount = computed(() => this.titleLength() > 140 * 0.8);
  readonly showDescriptionCount = computed(() => this.descriptionLength() > 5000 * 0.8);

  /**
   * An idea is not a defect: there is nothing to reproduce, and no expected-vs-actual.
   * Suppressing that whole block halves the form for these submitters.
   */
  readonly isIdea = computed(() => IDEA_TYPE_VALUES.has(this.issueType()));

  /**
   * Report quality — shown live while typing. This is not a gamification gimmick: a report
   * carrying steps and a screenshot is one an engineer can act on without a round-trip, so
   * nudging for them here is the cheapest possible triage win.
   */
  readonly qualityChecks = computed(() => {
    const desc = (this.descriptionValue() ?? '').trim();
    const steps = (this.stepsValue() ?? '').trim();
    const checks = [
      { label: 'A clear title', done: (this.titleValue() ?? '').trim().length >= 10 },
      { label: 'A description with some detail', done: desc.length >= 40 },
      { label: 'Where it happened', done: !!this.moduleValue() },
      { label: 'A screenshot', done: this.attachments().length > 0 },
    ];
    // Steps only count toward quality where steps are meaningful.
    if (!this.isIdea()) {
      checks.splice(3, 0, { label: 'Steps to reproduce', done: steps.length >= 10 });
    }
    return checks;
  });

  readonly qualityScore = computed(() => {
    const checks = this.qualityChecks();
    return Math.round((checks.filter((c) => c.done).length / checks.length) * 100);
  });

  readonly qualityLabel = computed(() => {
    const s = this.qualityScore();
    if (s >= 100) return 'Great report';
    if (s >= 60) return 'Good — a bit more would help';
    if (s >= 30) return 'Needs more detail';
    return 'Add some detail to help us';
  });

  readonly showTaxFields = computed(() => this.issueType() === 'tax_calculation_issue');
  readonly showSyncFields = computed(() => this.issueType() === 'sync_issue');
  readonly showPricingFields = computed(() => this.issueType() === 'pricing_issue');
  readonly showPerformanceFields = computed(() => this.issueType() === 'performance_issue');

  readonly accountEmail = computed(() => this.auth.currentUser()?.email ?? '—');
  readonly accountName = computed(() => this.auth.currentUser()?.name ?? '—');
  readonly jurisdiction = computed(() => this.auth.currentUser()?.country ?? '—');

  /**
   * The Tier 2 values, shown verbatim in the disclosure panel. §1.6 requires the panel
   * to list the ACTUAL values that would be sent — a generic "we may collect wallet
   * details" is not informed consent.
   */
  readonly walletAddresses = computed(() =>
    this.connections()
      .filter((c) => c.connection_type === 'chain' && !!c.wallet_address)
      .map((c) => c.wallet_address as string)
  );

  readonly exchangeNames = computed(() =>
    this.connections()
      .filter((c) => c.connection_type === 'exchange')
      .map((c) => c.label)
  );

  readonly hasTier2Data = computed(
    () => this.walletAddresses().length > 0 || this.exchangeNames().length > 0
  );

  readonly selectedConnection = computed(() => {
    const id = this.form.controls.connection_id.value;
    return this.connections().find((c) => c._id === id) ?? null;
  });

  readonly totalAttachmentBytes = computed(() =>
    this.attachments().reduce((sum, a) => sum + a.file.size, 0)
  );

  readonly anyUploading = computed(() => this.attachments().some((a) => a.uploading));

  /**
   * True while an upload or the submit itself is in flight.
   *
   * The modal reads this to refuse an accidental close (Escape / backdrop click) mid-flight.
   * It matters most on submit: destroying the form cancels the POST client-side, but that
   * does NOT roll back the server — the issue may already be created. The user would see no
   * reference number, assume it failed, and file the whole thing again.
   */
  readonly busy = computed(() => this.submitting() || this.anyUploading());

  constructor() {
    // Signal inputs are NOT populated during construction — reading initialModule()
    // here would always see the `null` default and silently defeat the route pre-fill,
    // which is the entire reason the in-page button exists. An effect runs after the
    // input is bound. The `!module.value` guard means a later navigation can't clobber
    // a module the user has since picked by hand.
    effect(() => {
      // The modal passes the module of the page it was opened over. The standalone Report
      // page can't — by then the route IS /app/support/report — so it falls back to the
      // last product route the user was on. Without the fallback that field stays
      // "Not specified", which is exactly the signal triage needs most.
      const initial = this.initialModule() ?? this.lastModuleTracker.lastModule();
      if (initial && !this.form.controls.module.value) {
        this.form.controls.module.setValue(initial);
      }
    });

    this.form.controls.issue_type.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((t) => {
        this.issueType.set((t as IssueType) ?? 'bug');
        this.applyConditionalValidators();
        if (t === 'performance_issue' && !this.form.controls.occurred_at.value) {
          this.form.controls.occurred_at.setValue(toLocalDateTimeInput(new Date()));
        }
      });

    // These two run in the constructor, so they're in an injection context and
    // takeUntilDestroyed() needs no explicit DestroyRef. Bound to the component's life so
    // a modal closed before they land doesn't write to signals on a dead component.
    //
    // Loaded for the consent panel (real values) and the Sync-Issue picker (the user's
    // OWN connections only — never a global list).
    this.integrationsApi
      .getConnections()
      .pipe(
        catchError(() => of({ data: [] as UserConnection[] })),
        takeUntilDestroyed(),
      )
      .subscribe((res) => this.connections.set(res.data ?? []));

    this.taxApi
      .getCryptoTaxOptions()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(),
      )
      .subscribe((res: any) => {
        const country = this.auth.currentUser()?.country;
        const byCountry = res?.data?.supportedYearsByCountry ?? res?.supportedYearsByCountry;
        const years: number[] =
          (country && byCountry?.[country]) ??
          (byCountry ? Object.values(byCountry).flat() as number[] : []);
        this.taxYears.set([...new Set(years)].sort((a, b) => b - a));
        if (country) this.form.controls.jurisdiction.setValue(country);
      });

    this.destroyRef.onDestroy(() => this.revokeAllPreviews());
  }

  /**
   * Mirrors the server's per-type requirements (§1.7). Without these, every tax or sync
   * bug starts with three rounds of "which tax year?" / "which wallet?".
   */
  private applyConditionalValidators(): void {
    const c = this.form.controls;
    const conditional = [c.tax_year, c.jurisdiction, c.connection_id, c.asset, c.date];
    conditional.forEach((ctrl) => ctrl.clearValidators());

    switch (this.issueType()) {
      case 'tax_calculation_issue':
        c.tax_year.setValidators([Validators.required]);
        c.jurisdiction.setValidators([Validators.required]);
        break;
      case 'sync_issue':
        c.connection_id.setValidators([Validators.required]);
        break;
      case 'pricing_issue':
        c.asset.setValidators([Validators.required]);
        c.date.setValidators([Validators.required]);
        break;
    }

    conditional.forEach((ctrl) => ctrl.updateValueAndValidity({ emitEvent: false }));
  }

  // ------------------------------------------------------------------ issue type

  /**
   * The type picker is a button grid rather than a select, so it writes to the control by
   * hand. setValue() still fires valueChanges, which is what drives the conditional
   * validators and the context panel below.
   */
  selectType(value: string): void {
    this.form.controls.issue_type.setValue(value as IssueType);
    this.form.controls.issue_type.markAsTouched();
  }

  isType(value: string): boolean {
    return this.form.controls.issue_type.value === value;
  }

  // ------------------------------------------------------------------ attachments

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = ''; // let the same file be re-picked after a remove
    this.acceptFiles(files);
  }

  /**
   * Paste-to-attach. Nobody saves a screenshot to disk and then browses for it — they hit
   * PrtSc and Ctrl+V. Bound at the form level so it works wherever the caret is, and it
   * ignores plain-text pastes so typing into the description is unaffected.
   */
  onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.items ?? [])
      .filter((i) => i.kind === 'file')
      .map((i) => i.getAsFile())
      .filter((f): f is File => !!f && f.type.startsWith('image/'));

    if (!files.length) return;
    event.preventDefault();
    this.acceptFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(true);
  }

  onDragLeave(): void {
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
    this.acceptFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  private acceptFiles(files: File[]): void {
    for (const file of files) {
      const rejection = this.rejectionReason(file);
      if (rejection) {
        this.toast.error(rejection, 'Attachment rejected');
        continue;
      }
      this.addAndUpload(file);
    }
  }

  /**
   * Auto-numbering: pressing Enter in an already-numbered list continues it. Plain Enter in
   * an unnumbered box stays a plain newline — we don't force structure on someone who is
   * writing prose.
   */
  onStepsKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;

    const el = event.target as HTMLTextAreaElement;
    const upToCaret = el.value.slice(0, el.selectionStart);
    const currentLine = upToCaret.slice(upToCaret.lastIndexOf('\n') + 1);
    const match = /^(\d+)\.\s/.exec(currentLine);
    if (!match) return;

    // "3. " with nothing after it means the user is done listing — end the list.
    if (currentLine.trim() === `${match[1]}.`) {
      event.preventDefault();
      const start = el.selectionStart - currentLine.length;
      const next = el.value.slice(0, start) + el.value.slice(el.selectionStart);
      this.form.controls.steps_to_reproduce.setValue(next);
      return;
    }

    event.preventDefault();
    const insert = `\n${Number(match[1]) + 1}. `;
    const next = el.value.slice(0, el.selectionStart) + insert + el.value.slice(el.selectionEnd);
    this.form.controls.steps_to_reproduce.setValue(next);
    // Restore the caret after Angular writes the new value back into the textarea.
    const caret = el.selectionStart + insert.length;
    setTimeout(() => el.setSelectionRange(caret, caret));
  }

  /** Seeds "1. " so the numbering has something to continue from. */
  onStepsFocus(): void {
    if (!this.form.controls.steps_to_reproduce.value) {
      this.form.controls.steps_to_reproduce.setValue('1. ');
    }
  }

  /**
   * Take the seed back out again if the user never wrote a step. Without this, focusing the
   * field and moving on leaves "1. " in the control — which reads as filled-in, marks the
   * form dirty, and (before the buildPayload guard) shipped "1." to support as the sole
   * reproduction step.
   */
  onStepsBlur(): void {
    const value = this.form.controls.steps_to_reproduce.value ?? '';
    if (STEPS_PLACEHOLDER_ONLY.test(value)) {
      this.form.controls.steps_to_reproduce.setValue('');
    }
  }

  /**
   * Client-side checks are a CONVENIENCE — fast feedback, nothing more. The server
   * re-validates every one of them from the file's magic bytes, because a MIME type
   * reported by the browser is just a claim.
   */
  private rejectionReason(file: File): string | null {
    if (this.attachments().length >= MAX_ATTACHMENTS) {
      return `You can attach at most ${MAX_ATTACHMENTS} images.`;
    }
    if (!ACCEPTED_MIMES.includes(file.type as (typeof ACCEPTED_MIMES)[number])) {
      return `${file.name} is not a PNG, JPEG or WebP image.`;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      return `${file.name} is larger than 5 MB.`;
    }
    if (this.totalAttachmentBytes() + file.size > MAX_ATTACHMENT_TOTAL_BYTES) {
      return 'Your attachments would exceed the 15 MB total limit.';
    }
    return null;
  }

  private addAndUpload(file: File): void {
    const pending: PendingAttachment = {
      file,
      previewUrl: URL.createObjectURL(file),
      uploading: true
    };
    this.attachments.update((list) => [...list, pending]);


    this.api
      .uploadAttachment(file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (uploaded) =>
          this.attachments.update((list) =>
            list.map((a) => (a === pending ? { ...a, uploading: false, uploaded } : a))
          ),
        error: (err) => {
          const message =
            err?.error?.metadata?.message ?? 'Upload failed. Please try again.';
          this.attachments.update((list) =>
            list.map((a) => (a === pending ? { ...a, uploading: false, error: message } : a))
          );
          this.toast.error(message, 'Upload failed');
        }
      });
  }

  removeAttachment(target: PendingAttachment): void {
    URL.revokeObjectURL(target.previewUrl);
    this.attachments.update((list) => list.filter((a) => a !== target));
  }

  private revokeAllPreviews(): void {
    this.attachments().forEach((a) => URL.revokeObjectURL(a.previewUrl));
  }

  // ---------------------------------------------------------------------- submit

  toggleDetails(): void {
    this.detailsExpanded.update((v) => !v);
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.error('Please complete the required fields.', 'Incomplete');
      return;
    }
    if (this.anyUploading()) {
      this.toast.info('Please wait for your attachments to finish uploading.');
      return;
    }

    this.submitting.set(true);
    const payload = this.buildPayload();

    this.api
      .createIssue(payload)
      .pipe(
        finalize(() => this.submitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (created) => this.submitted.emit(created),
        error: (err) => {
          const message =
            err?.error?.metadata?.message ??
            'We could not submit your report. Please try again.';
          this.toast.error(message, 'Submission failed');
        }
      });
  }

  private buildPayload(): CreateIssueRequest {
    const v = this.form.getRawValue();
    const consent = v.technical_details_consent === true;

    const context = this.buildContext(v);

    const payload: CreateIssueRequest = {
      title: v.title!.trim(),
      issue_type: v.issue_type as IssueType,
      description: v.description!.trim(),
      ...this.env,
      technical_details_consent: consent
    };

    if (v.module) payload.module = v.module as IssueModule;
    // Not just `if (trim())` — a bare list marker ("1.") is the seed, not a step. onStepsBlur
    // normally strips it; this is the backstop for the paths that skip blur, e.g. hitting
    // Enter to submit while the caret is still in the empty steps box.
    const steps = v.steps_to_reproduce?.trim();
    if (steps && !STEPS_PLACEHOLDER_ONLY.test(steps)) payload.steps_to_reproduce = steps;
    if (v.expected_behavior?.trim()) payload.expected_behavior = v.expected_behavior.trim();
    if (v.actual_behavior?.trim()) payload.actual_behavior = v.actual_behavior.trim();
    if (v.contact_email_hint?.trim()) payload.contact_email_hint = v.contact_email_hint.trim();
    if (Object.keys(context).length) payload.context = context;

    const ids = this.attachments()
      .map((a) => a.uploaded?.attachment_id)
      .filter((id): id is string => !!id);
    if (ids.length) payload.attachments = ids;

    // Tier 2 is sent ONLY on explicit opt-in. When the box is unticked these keys are
    // absent from the payload entirely — and the server will not backfill them from the
    // user's connections either, so unticking genuinely means "do not collect".
    if (consent) {
      const wallets = this.walletAddresses();
      const exchanges = this.exchangeNames();
      if (wallets.length) payload.wallet_address = wallets;
      if (exchanges.length) payload.exchange = exchanges;
    }

    return payload;
  }

  private buildContext(v: ReturnType<typeof this.form.getRawValue>): IssueContext {
    const ctx: IssueContext = {};

    switch (this.issueType()) {
      case 'tax_calculation_issue':
        if (v.tax_year) ctx.tax_year = Number(v.tax_year);
        if (v.jurisdiction) ctx.jurisdiction = v.jurisdiction;
        if (v.report_id?.trim()) ctx.report_id = v.report_id.trim();
        break;

      case 'sync_issue': {
        const conn = this.selectedConnection();
        if (v.connection_id) ctx.connection_id = v.connection_id;
        // Auto-attached from the selected connection — read-only, so support does not
        // have to ask "when did it last sync?".
        if (conn?.last_synced_at) ctx.last_sync_at = conn.last_synced_at;
        if (conn?.chain?.name) ctx.chain = conn.chain.name;
        break;
      }

      case 'pricing_issue':
        if (v.asset?.trim()) ctx.asset = v.asset.trim();
        if (v.date) ctx.date = new Date(v.date).toISOString();
        if (v.expected_price_source?.trim()) {
          ctx.expected_price_source = v.expected_price_source.trim();
        }
        break;

      case 'performance_issue':
        if (v.occurred_at) ctx.occurred_at = new Date(v.occurred_at).toISOString();
        break;
    }

    return ctx;
  }
}

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in LOCAL time, not an ISO/UTC string. */
function toLocalDateTimeInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
